import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import useUser from '../../auth/useUser';
import { novaChat, novaChatReport, novaGenerateReport, novaGenerateReportAsync, novaReportStatus, novaDownloadReport, novaSaveSession, novaListSessions, novaGetSession, novaDeleteSession, novaRenameSession, novaFileCompareStart, novaFileCompareStatus, novaStartMarketReport, novaMarketReportStatus } from '../../services/novaService';
import DOMPurify from 'dompurify';
import { buildNovaSummaryHtml } from '../../utils/cockpitExport';
import useSpeechInput from '../../hooks/useSpeechInput';
import './NovaPanel.css';

// Ensure all sanitized links open in new tabs safely
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/** Set to false to hide the Dynamic style option and always use FocusOne */
const DYNAMIC_STYLE_ENABLED = true;

const REPORT_KEYWORDS_RE = /\b(report|analysis|analy[sz]e|data\s*analysis|pptx|powerpoint|power\s*point|excel|xlsx|trend\s*report|developments?\s*report|market\s*analysis|therapeutic\s*area\s*report|generate\s*report)\b/i;
const PRODUCT_KEYWORDS_RE = /\b(products?|product\s*list|show\s+suppliers?|list\s+suppliers?|supplier\s+products?)\b/i;
const AMBIGUOUS_INTENTS = new Set(['atc_search', 'molecule']);
const COMPARE_INTENT_RE = /\b(compare|comparing|comparison|comparisation|comparision|comparsion|comparaison|vs\.?|versus)\b/i;

/** Words that are compare-intent filler — never entity names */
const COMPARE_FILLER_WORDS = new Set([
  'compare', 'comparing', 'comparison', 'comparisation', 'comparision',
  'comparsion', 'comparaison',
]);

/** Kind-word map: user text → canonical compareKind */
const COMPARE_KIND_MAP = {
  molecule: 'molecule', molecules: 'molecule',
  supplier: 'supplier', suppliers: 'supplier',
  area: 'area', areas: 'area',
  'therapeutic area': 'area', 'therapeutic areas': 'area',
};
const COMPARE_KIND_WORDS = new Set(Object.keys(COMPARE_KIND_MAP));

/** Shared helper: extract (kind, parts) from free-text, stripping filler + kind words. */
function extractCompareNames(text) {
  let s = String(text).trim();
  // Strip lead-in phrases
  s = s.replace(/^(?:i\s+(?:want|need|would\s+like)\s+(?:to\s+|a\s+)?)?/i, '');
  s = s.replace(/^(?:please\s+|can\s+you\s+|could\s+you\s+)?/i, '');
  s = s.replace(/^(?:compar(?:e|ing|ison|isation|ision|sion|aison)(?:\s+of)?)\s*/i, '');
  // Strip add-another filler
  s = s.replace(/^(?:add|also\s+add|include|also|and)\s+/i, '');
  s = s.replace(/\s+(?:as\s+well|too|also|please)$/i, '');
  s = s.trim();

  // Detect and strip kind word from the front (e.g. "molecules paracetamol and ibuprofen")
  let detectedKind = null;
  const lower = s.toLowerCase();
  // Check multi-word kinds first ("therapeutic areas")
  for (const kw of ['therapeutic areas', 'therapeutic area']) {
    if (lower.startsWith(kw)) {
      detectedKind = COMPARE_KIND_MAP[kw];
      s = s.slice(kw.length).trim();
      break;
    }
  }
  if (!detectedKind) {
    for (const kw of COMPARE_KIND_WORDS) {
      if (kw.includes(' ')) continue; // already checked multi-word
      if (lower === kw || lower.startsWith(kw + ' ') || lower.startsWith(kw + ',')) {
        detectedKind = COMPARE_KIND_MAP[kw];
        s = s.slice(kw.length).replace(/^[\s,]+/, '').trim();
        break;
      }
    }
  }

  if (!s) return { kind: detectedKind, parts: [] };
  // Split on commas (optional surrounding whitespace) OR word separators (vs/and/with/&)
  const parts = s.split(/\s*,\s*|\s+(?:vs\.?|versus|and|with|&)\s+/i).map(p => p.trim()).filter(Boolean);
  // Guard: filter out kind words AND compare-intent filler words that survived splitting
  const cleanParts = parts.filter(p => {
    const lp = p.toLowerCase();
    return !COMPARE_KIND_WORDS.has(lp) && !COMPARE_FILLER_WORDS.has(lp);
  });
  // De-duplicate case-insensitively, keep first occurrence
  const seen = new Set();
  const unique = [];
  for (const p of cleanParts) {
    const key = p.toLowerCase();
    if (!seen.has(key)) { seen.add(key); unique.push(p); }
  }
  return { kind: detectedKind, parts: unique };
}

const CHIPS = [
  { ico: '🏢', label: 'Supplier details', q: 'gimme details of Adalvo' },
  { ico: '📞', label: 'Contact info', q: 'who is the contact for Adalvo' },
  { ico: '💊', label: 'Molecule search', action: 'molecule-search' },
  { ico: '📥', label: 'Export CSV', action: 'export-csv' },
];

const DATA_PAGES = ['/supplier-products', '/suppliers', '/ema-supps', '/dutch-supps', '/ansm-supps'];

/** Status messages shown during report polling, keyed by elapsed-second thresholds */
const REPORT_PROGRESS_STAGES = [
  { after: 0,   text: 'Gathering data from the database\u2026' },
  { after: 15,  text: 'Researching public sources on the web\u2026' },
  { after: 75,  text: 'Designing your slides\u2026' },
  { after: 230, text: 'Reviewing the layout\u2026' },
  { after: 330, text: 'Finalizing your file\u2026' },
];

function getTimeGreeting() {
  const d = new Date();
  const hm = d.getHours() * 60 + d.getMinutes();
  if (hm >= 5 * 60 + 30 && hm <= 11 * 60 + 30) return 'Good morning';
  if (hm >= 11 * 60 + 31 && hm <= 17 * 60) return 'Good afternoon';
  if (hm >= 17 * 60 + 1 && hm <= 21 * 60 + 30) return 'Good evening';
  return 'Good night';
}

function pageLabelFor(url) {
  if (!url) return '';
  if (url.startsWith('/suppliers-list')) return 'Supplier Directory';
  if (url.startsWith('/supplier-products')) return 'All Supplier Products';
  if (url.startsWith('/suppliers')) return 'Suppliers';
  return url;
}

function filterDescription(filter) {
  if (!filter) return '';
  const parts = [];
  if (filter.supplier) parts.push(`supplier: ${filter.supplier}`);
  if (filter.search) parts.push(`search: ${filter.search}`);
  if (filter.form) parts.push(`form: ${filter.form}`);
  if (filter.ma) parts.push(`MA: ${filter.ma}`);
  if (filter.dossier) parts.push(`dossier: ${filter.dossier}`);
  if (parts.length === 0) return '';
  return ` (${parts.join(', ')})`;
}

function buildCsvString(csv) {
  const headers = (csv.headers || []).join(',');
  const body = (csv.rows || []).map(row =>
    row.map(cell => {
      if (cell === null || cell === undefined) return '';
      const s = String(cell);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    }).join(',')
  ).join('\n');
  return `${headers}\n${body}`;
}

function triggerCsvDownload(csv) {
  const csvContent = buildCsvString(csv);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = csv.filename || 'nova_export.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// quickRepliesFor removed — Nova now returns text-only responses (no suggestion pills)

function SupplierButtons({ suppliers, molecule, onSelect, onDownloadAll }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--t3, #6B7280)', marginBottom: 8 }}>Select a supplier to filter:</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <button
          type="button"
          onClick={() => onSelect('all', molecule)}
          style={{ background: 'var(--teal)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
        >
          Display all of them
        </button>
        <button
          type="button"
          onClick={() => onDownloadAll(molecule)}
          style={{ background: 'var(--navy)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
        >
          Download all in CSV
        </button>
        {suppliers.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => onSelect(s, molecule)}
            style={{
              background: 'var(--card)', color: 'var(--teal)',
              border: '1px solid var(--teal)',
              borderRadius: 20, padding: '5px 14px',
              fontSize: 12, cursor: 'pointer',
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function DisambiguateButtons({ options, searchTerm, onSelect }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--t3, #6B7280)', marginBottom: 8 }}>Which field would you like to filter by?</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map(o => (
          <button
            key={o.field}
            type="button"
            onClick={() => onSelect(o, searchTerm)}
            style={{
              background: 'var(--card)', color: 'var(--teal)',
              border: '1px solid var(--teal)',
              borderRadius: 20, padding: '5px 14px',
              fontSize: 12, cursor: 'pointer',
            }}
          >
            Filter by {o.label} ({o.count})
          </button>
        ))}
      </div>
    </div>
  );
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatSessionTitle(createdAt, firstUserMsg) {
  const d = createdAt ? new Date(createdAt) : new Date();
  const prefix = `${d.getFullYear()}-${MONTH_NAMES[d.getMonth()]}-${String(d.getDate()).padStart(2,'0')}`;
  if (!firstUserMsg) return `${prefix}-New chat`;
  const clean = String(firstUserMsg).replace(/[\r\n]+/g, ' ').trim().slice(0, 40);
  return `${prefix}-${clean}`;
}

function buildInitialMessages(firstName) {
  return [{ role: 'assistant', content: `${getTimeGreeting()}, ${firstName},` }];
}

export default function NovaPanel({ open, onClose, expanded = false }) {
  const { displayName } = useUser();
  const { instance: msalInstance } = useMsal();
  const firstName = (displayName || 'there').split(' ')[0];
  const [messages, setMessages] = useState(() => buildInitialMessages(firstName));
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [novaContext, setNovaContext] = useState({
    currentSupplier: null,
    currentMolecule: null,
    lastIntent: null,
    awaitingReply: null,
    availableSuppliers: [],
  });
  const [reportGenerating, setReportGenerating] = useState({});
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [sessionCreatedAt, setSessionCreatedAt] = useState(() => new Date().toISOString());
  const sessionReportsRef = useRef([]);
  const titleCustomRef = useRef(false);
  const [recentSessions, setRecentSessions] = useState([]);
  const [sideOpen, setSideOpen] = useState(false);
  const [kebabOpen, setKebabOpen] = useState(null); // sessionId or null
  const [bandRenaming, setBandRenaming] = useState(null); // sessionId or null
  const [bandRenameVal, setBandRenameVal] = useState('');
  const mountedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const pendingFileRef = useRef(null);
  const fileSupplierNameRef = useRef(null);
  const fileInputRef = useRef(null);
  const msgsRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Speech-to-text (fills the input box for review; does NOT auto-send)
  // Mic language is independent of report language — defaults to English, toggleable per session
  const [micLang, setMicLang] = useState('en-US');
  const speechFinalBuf = useRef('');
  const speech = useSpeechInput({
    lang: micLang,
    onInterim: (text) => setInput(() => speechFinalBuf.current + text),
    onFinal: (text) => {
      speechFinalBuf.current += text;
      setInput(speechFinalBuf.current);
    },
  });
  const toggleMic = useCallback(() => {
    if (speech.listening) {
      speech.stop();
    } else {
      speechFinalBuf.current = input;
      speech.start();
    }
  }, [speech, input]);
  const toggleMicLang = useCallback(() => {
    if (speech.listening) speech.stop();
    setMicLang(prev => prev === 'en-US' ? 'nl-NL' : 'en-US');
  }, [speech]);

  // Report-progress state: { key, startedAt } or null
  const [reportProgress, setReportProgress] = useState(null);
  const reportProgressTimerRef = useRef(null);

  // Autosave helper — debounced, non-blocking
  const scheduleAutosave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      setMessages(curMsgs => {
        setNovaContext(curCtx => {
          const hasUserMsg = curMsgs.some(m => m.role === 'user');
          if (!hasUserMsg) return curCtx;
          const firstUserMsg = curMsgs.find(m => m.role === 'user');
          const title = titleCustomRef.current
            ? undefined // let backend keep the custom title
            : formatSessionTitle(sessionCreatedAt, firstUserMsg?.content);
          novaSaveSession(msalInstance, {
            sessionId,
            title: title || formatSessionTitle(sessionCreatedAt, firstUserMsg?.content),
            titleCustom: titleCustomRef.current,
            messages: curMsgs,
            novaContext: curCtx,
            reports: sessionReportsRef.current,
            createdAt: sessionCreatedAt,
          }).catch(() => {});
          return curCtx;
        });
        return curMsgs;
      });
    }, 1000);
  }, [msalInstance, sessionId, sessionCreatedAt]);

  // Immediate (non-debounced) save — used when opening a cockpit so it survives a refresh
  const saveSessionNow = useCallback((msgs, ctx) => {
    try {
      if (!msgs || !msgs.some(m => m.role === 'user')) return;
      const firstUserMsg = msgs.find(m => m.role === 'user');
      novaSaveSession(msalInstance, {
        sessionId,
        title: formatSessionTitle(sessionCreatedAt, firstUserMsg?.content),
        titleCustom: titleCustomRef.current,
        messages: msgs,
        novaContext: ctx,
        reports: sessionReportsRef.current,
        createdAt: sessionCreatedAt,
      }).catch(() => {});
    } catch { /* ignore */ }
  }, [msalInstance, sessionId, sessionCreatedAt]);

  // Load recent sessions for the left band
  const refreshRecentSessions = useCallback(async () => {
    try {
      const list = await novaListSessions(msalInstance);
      setRecentSessions(list.slice(0, 5));
    } catch { setRecentSessions([]); }
  }, [msalInstance]);

  // Helper to load a session by ID
  const loadSession = useCallback(async (sid) => {
    try {
      const full = await novaGetSession(msalInstance, sid);
      setSessionId(full.sessionId);
      setSessionCreatedAt(full.createdAt || new Date().toISOString());
      setMessages(full.messages || buildInitialMessages(firstName));
      setNovaContext(full.novaContext || { currentSupplier: null, currentMolecule: null, lastIntent: null, awaitingReply: null, availableSuppliers: [] });
      sessionReportsRef.current = full.reports || [];
      titleCustomRef.current = full.titleCustom || false;
      return true;
    } catch { return false; }
  }, [msalInstance, firstName]);

  // On open: resume most recent session (or novaResumeSession from sessionStorage)
  useEffect(() => {
    if (!open) return;
    if (mountedRef.current) return; // only first open
    mountedRef.current = true;
    (async () => {
      // Check for explicit resume request from /chat-history "Continue" button
      const resumeId = sessionStorage.getItem('novaResumeSession');
      if (resumeId) {
        sessionStorage.removeItem('novaResumeSession');
        const ok = await loadSession(resumeId);
        if (ok) { refreshRecentSessions(); return; }
      }
      // Auto-load the most recent session
      try {
        const list = await novaListSessions(msalInstance);
        setRecentSessions(list.slice(0, 5));
        if (list.length > 0) {
          await loadSession(list[0].sessionId);
        }
      } catch {}
    })();
  }, [open, msalInstance, loadSession, refreshRecentSessions]);

  // Listen for novaResumeSession changes while already open
  useEffect(() => {
    const handler = () => {
      const resumeId = sessionStorage.getItem('novaResumeSession');
      if (resumeId) {
        sessionStorage.removeItem('novaResumeSession');
        scheduleAutosave();
        loadSession(resumeId).then(() => refreshRecentSessions());
      }
    };
    window.addEventListener('novaOpenPanel', handler);
    return () => window.removeEventListener('novaOpenPanel', handler);
  }, [loadSession, scheduleAutosave, refreshRecentSessions]);

  useEffect(() => () => {
    if (reportProgressTimerRef.current) clearInterval(reportProgressTimerRef.current);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  // Resume polling for an in-flight report job after remount
  useEffect(() => {
    if (!open) return;
    const raw = sessionStorage.getItem('novaInflightJob');
    if (!raw) return;
    let inflight;
    try { inflight = JSON.parse(raw); } catch { sessionStorage.removeItem('novaInflightJob'); return; }
    // Abandon if older than 14 minutes (beyond max budget)
    if (Date.now() - inflight.startedAt > 14 * 60 * 1000) { sessionStorage.removeItem('novaInflightJob'); return; }
    const { processId, action: savedAction, key: savedKey } = inflight;
    setReportGenerating(prev => ({ ...prev, [savedKey]: true }));
    setMessages(cur => [...cur, { role: 'assistant', content: 'Resuming report generation…' }]);
    let cancelled = false;
    (async () => {
      try {
        let meta = null;
        const remaining = Math.max(60, Math.ceil((14 * 60 * 1000 - (Date.now() - inflight.startedAt)) / 2000));
        for (let i = 0; i < remaining && !cancelled; i++) {
          await new Promise(r => setTimeout(r, 2000));
          try {
            const st = await novaReportStatus(msalInstance, processId);
            if (st.status === 'done' && st.id) { meta = st; break; }
            if (st.status === 'failed') { throw new Error(st.error || 'Report generation failed'); }
          } catch (e) {
            if (e?.message?.includes('Report generation failed')) throw e;
            // Swallow transient / 401 errors and keep polling
          }
        }
        sessionStorage.removeItem('novaInflightJob');
        if (meta && meta.id) {
          sessionReportsRef.current = [...sessionReportsRef.current, { id: meta.id, filename: meta.filename, reportType: savedAction.reportType, createdAt: new Date().toISOString() }];
          const blob = await novaDownloadReport(msalInstance, meta.id);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = meta.filename || `report.${savedAction.format}`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `Report ready: **${meta.filename || 'report.' + savedAction.format}**`,
            reportActions: [{ type: 'download_report', reportType: savedAction.reportType, format: savedAction.format, label: 'Download again', _blobId: meta.id, _filename: meta.filename || `report.${savedAction.format}` }],
          }]);
          scheduleAutosave();
        } else if (!cancelled) {
          setMessages(cur => [...cur, { role: 'assistant', content: 'Report generation timed out. Please try again.' }]);
        }
      } catch {
        sessionStorage.removeItem('novaInflightJob');
        setMessages(cur => [...cur, { role: 'assistant', content: 'Report generation failed. Please try again.' }]);
      } finally {
        setReportGenerating(prev => ({ ...prev, [savedKey]: false }));
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, msalInstance]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages, typing]);

  const runAction = useCallback((action) => {
    if (!action || !action.type) return null;
    if (action.type === 'navigate') {
      if (action.filter) {
        sessionStorage.setItem('novaFilter', JSON.stringify({ ...action.filter, timestamp: Date.now() }));
      }
      const targetUrl = action.url || '/';
      if (location.pathname === targetUrl) {
        window.dispatchEvent(new CustomEvent('novaFilterUpdate'));
      } else {
        navigate(targetUrl);
      }
      return `Navigated to ${pageLabelFor(targetUrl)}${filterDescription(action.filter)}.`;
    }
    if (action.type === 'open_supplier_modal') {
      if (action.supplier) {
        sessionStorage.setItem('novaOpenSupplier', action.supplier);
      }
      navigate('/suppliers-list');
      return `Opened ${action.supplier || 'supplier'} profile on the Supplier Directory.`;
    }
    if (action.type === 'download_csv') {
      // Don't auto-execute downloads — a button is rendered in the message instead
      return null;
    }
    return null;
  }, [navigate, location.pathname]);

  const handleReportDownload = useCallback(async (action, msgIdx) => {
    const key = `${msgIdx}-${action.format}${action.style ? '-' + action.style : ''}`;
    setReportGenerating(prev => ({ ...prev, [key]: true }));
    try {
      // If the report is already generated and stored (e.g. file-compare), download directly
      if (action._blobId) {
        const blob = await novaDownloadReport(msalInstance, action._blobId);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = action._filename || `report.${action.format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return;
      }
      const filters = { areas: action.areas || [], forms: action.forms || [] };
      if (action.supplier) filters.supplier = action.supplier;
      if (action.inn) filters.inn = action.inn;
      if (action.predictiveStatus) filters.status = action.predictiveStatus;
      if (action.predictiveSupplier) filters.supplier = action.predictiveSupplier;
      if (action.predictiveInn) filters.inn = action.predictiveInn;
      if (action.nlGapRegister) filters.register = action.nlGapRegister;
      if (action.nlGapSupplier) filters.supplier = action.nlGapSupplier;
      if (action.nlGapInn) filters.inn = action.nlGapInn;
      if (action.compareSuppliers) filters.suppliers = action.compareSuppliers;
      if (action.compareMolecules) filters.molecules = action.compareMolecules;
      if (action.compareAreas) filters.areas = action.compareAreas;
      // Submit async (agent or inline fallback)
      const submitRes = await novaGenerateReportAsync(msalInstance, {
        reportType: action.reportType,
        format: action.format,
        filters,
        language: action.language || undefined,
        style: action.style || undefined,
      });
      // Handle validation / disambiguation responses
      if (submitRes.needsDisambiguation && submitRes.disambiguation) {
        const lines = Object.entries(submitRes.disambiguation).map(([term, candidates]) =>
          `"${term}" — did you mean: ${candidates.join(', ')}?`
        ).join('\n');
        const extra = (submitRes.unknown || []).length > 0 ? `\nNot found: ${submitRes.unknown.join(', ')}.` : '';
        setMessages(cur => [...cur, {
          role: 'assistant',
          content: `I found possible matches:\n${lines}${extra}\nPlease pick the correct name:`,
          compareDisambiguation: { disambiguation: submitRes.disambiguation, resolved: submitRes.resolved || [], action },
        }]);
        return;
      }
      if (submitRes.notFound || submitRes.error) {
        setMessages(cur => [...cur, { role: 'assistant', content: submitRes.error || `Could not find: ${(submitRes.notFound || []).join(', ')}.` }]);
        return;
      }
      // If inline fallback returned a completed result, use it directly
      let meta = null;
      if (submitRes.status === 'done' && submitRes.id) {
        meta = submitRes;
      } else if (submitRes.process_id) {
        // Persist in-flight job so remounts can resume polling
        const inflightData = { processId: submitRes.process_id, action: { reportType: action.reportType, format: action.format, label: action.label }, key, startedAt: Date.now() };
        sessionStorage.setItem('novaInflightJob', JSON.stringify(inflightData));
        // Start live progress indicator
        const pollStartedAt = Date.now();
        setReportProgress({ key, startedAt: pollStartedAt });
        if (reportProgressTimerRef.current) clearInterval(reportProgressTimerRef.current);
        reportProgressTimerRef.current = setInterval(() => setReportProgress(prev => prev ? { ...prev } : null), 1000);
        // Poll for completion (agent path) — 12 min budget with 401 retry
        for (let pollN = 0; pollN < 360; pollN++) {
          await new Promise(r => setTimeout(r, 2000));
          try {
            const st = await novaReportStatus(msalInstance, submitRes.process_id);
            if (st.status === 'done' && st.id) { meta = st; break; }
            if (st.status === 'failed') { throw new Error(st.error || 'Report generation failed'); }
          } catch (pollErr) {
            // On 401 / token error, retry once with fresh token (acquireTokenSilent will refresh)
            if (pollErr?.response?.status === 401 || pollErr?.message?.includes('No authenticated account')) {
              try {
                await new Promise(r => setTimeout(r, 1000));
                const st = await novaReportStatus(msalInstance, submitRes.process_id);
                if (st.status === 'done' && st.id) { meta = st; break; }
                if (st.status === 'failed') { throw new Error(st.error || 'Report generation failed'); }
              } catch (retryErr) {
                if (retryErr?.message?.includes('Report generation failed')) throw retryErr;
                // Swallow transient errors and keep polling
              }
            } else {
              throw pollErr;
            }
          }
        }
        if (reportProgressTimerRef.current) { clearInterval(reportProgressTimerRef.current); reportProgressTimerRef.current = null; }
        setReportProgress(null);
        sessionStorage.removeItem('novaInflightJob');
        if (!meta) throw new Error('Report generation timed out');
      } else if (submitRes.id) {
        meta = submitRes;
      }
      if (!meta || !meta.id) {
        setMessages(cur => [...cur, { role: 'assistant', content: submitRes.error || 'Report generation returned an unexpected response.' }]);
        return;
      }
      // Track + download
      sessionReportsRef.current = [...sessionReportsRef.current, { id: meta.id, filename: meta.filename, reportType: action.reportType, createdAt: new Date().toISOString() }];
      const blob = await novaDownloadReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = meta.filename || `report.${action.format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Show "Report ready" message with manual re-download button
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: `Report ready: **${meta.filename || 'report.' + action.format}**`,
        reportActions: [{ type: 'download_report', reportType: action.reportType, format: action.format, label: 'Download again', _blobId: meta.id, _filename: meta.filename || `report.${action.format}` }],
      }]);
      scheduleAutosave();

      // Navigate to the relevant analytics page after download
      const rt = action.reportType || '';
      const isComparison = rt.endsWith('-comparison');
      const REPORT_ROUTE_MAP = {
        'therapeutic-area': '/therapeutic-area-developments',
        'area-comparison': '/therapeutic-area-developments',
        'molecule': '/molecule-developments',
        'molecule-comparison': '/molecule-developments',
        'pharmaceutical-form': '/pharmaceutical-form-developments',
        'supplier-portfolio': '/supplier-portfolio-evolution',
        'supplier-comparison': '/supplier-portfolio-evolution',
        'predictive': '/predictive-supplier-developments',
        'nl-gap': '/nl-market-gap-analysis',
      };
      const targetPath = REPORT_ROUTE_MAP[rt];
      if (targetPath) {
        let targetUrl = targetPath;
        if (!isComparison) {
          const qp = new URLSearchParams();
          if (rt === 'therapeutic-area' && action.areas?.length === 1) qp.set('area', action.areas[0]);
          else if (rt === 'molecule' && action.inn) qp.set('inn', action.inn);
          else if (rt === 'pharmaceutical-form' && action.forms?.length === 1) qp.set('form', action.forms[0]);
          else if (rt === 'supplier-portfolio' && action.supplier) qp.set('supplier', action.supplier);
          else if (rt === 'predictive' && (action.predictiveSupplier || action.supplier)) qp.set('supplier', action.predictiveSupplier || action.supplier);
          else if (rt === 'nl-gap' && (action.nlGapInn || action.inn)) qp.set('inn', action.nlGapInn || action.inn);
          const qs = qp.toString();
          if (qs) targetUrl += `?${qs}`;
        }
        navigate(targetUrl);
      }
    } catch {
      sessionStorage.removeItem('novaInflightJob');
      setMessages(cur => [...cur, { role: 'assistant', content: 'Report generation failed. Please try again.' }]);
    } finally {
      if (reportProgressTimerRef.current) { clearInterval(reportProgressTimerRef.current); reportProgressTimerRef.current = null; }
      setReportProgress(null);
      setReportGenerating(prev => ({ ...prev, [key]: false }));
    }
  }, [msalInstance, navigate, scheduleAutosave]);

  const executeRuleChatResponse = useCallback((res, { suppressNavigation = false } = {}) => {
    const assistantMsg = {
      role: 'assistant',
      content: res.message || '…',
      action: res.action || null,
      actions: res.actions || [],
      csvData: res.csvData || null,
      intent: res.intent || null,
      moleculeKeyword: res.moleculeKeyword || '',
      uniqueSuppliers: res.uniqueSuppliers || [],
      disambiguateOptions: res.disambiguateOptions || null,
      searchTerm: res.searchTerm || '',
    };
    setMessages(cur => [...cur, assistantMsg]);
    setNovaContext(prev => {
      const nextIntent = res.intent === 'molecule_from_supplier'
        ? 'molecule'
        : (res.intent || prev.lastIntent);
      return {
        currentSupplier: res.currentSupplier ?? prev.currentSupplier,
        currentMolecule: res.currentMolecule ?? prev.currentMolecule,
        lastIntent: nextIntent,
        awaitingReply: /would you like/i.test(res.message || '') ? res.message : null,
        availableSuppliers: res.uniqueSuppliers && res.uniqueSuppliers.length > 0
          ? res.uniqueSuppliers
          : prev.availableSuppliers,
      };
    });
    if (!suppressNavigation) {
      const allActions = res.actions || (res.action ? [res.action] : []);
      for (const act of allActions) {
        const actionNote = runAction(act);
        if (actionNote) {
          setMessages(cur => [...cur, { role: 'assistant', content: actionNote }]);
        }
      }
    }
    scheduleAutosave();
  }, [runAction, scheduleAutosave]);

  const callRuleChat = useCallback(async (nextHistory) => {
    const res = await novaChat({
      messages: nextHistory
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content })),
      user: { name: displayName || firstName },
      novaContext,
    });
    return res;
  }, [displayName, firstName, novaContext]);

  // ── File compare handler (must be declared before sendMessage which references it) ──
  const handleFileCompareSupplier = useCallback(async (text) => {
    // Two-step: first ask file supplier name, then target supplier
    if (novaContext.awaitingReply === 'file-compare-file-supplier') {
      fileSupplierNameRef.current = text;
      setMessages(cur => [...cur, { role: 'assistant', content: `Got it — "${text}". Now, which supplier would you like to compare against? Type the target supplier name:` }]);
      setNovaContext(prev => ({ ...prev, awaitingReply: 'file-compare-supplier' }));
      return;
    }
    // Step 2: target supplier — run the comparison
    const file = pendingFileRef.current;
    if (!file) return;
    const fileSupName = fileSupplierNameRef.current || file.name;
    pendingFileRef.current = null;
    fileSupplierNameRef.current = null;
    setMessages(cur => [...cur, { role: 'assistant', content: `Analyzing ${fileSupName} vs ${text}...` }]);
    setTyping(true);
    try {
      const startRes = await novaFileCompareStart(msalInstance, file, text, fileSupName);
      const processId = startRes.processId;
      let result = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const status = await novaFileCompareStatus(msalInstance, processId);
        if (status.status === 'done') { result = status; break; }
        if (status.status === 'failed') { throw new Error(status.error || 'Comparison failed'); }
      }
      if (!result) throw new Error('Comparison timed out');
      setTyping(false);
      const s = result.summary;
      const fName = result.fileSupplierName || fileSupName;
      const tName = result.supplierName;
      const summaryLines = [
        `Quick Comparison: ${fName} vs ${tName}`,
        `\u2022 Common molecules: ${s.common}`,
        `\u2022 Different (in ${fName}, not in ${tName}): ${s.differentFromFile}${result.differentList?.length > 0 ? ' (' + result.differentList.slice(0, 5).join(', ') + (s.differentFromFile > 5 ? '...' : '') + ')' : ''}`,
        `\u2022 Total from ${fName}: ${s.fileTotal} | Total from ${tName}: ${s.targetTotal}`,
      ].join('\n');
      if (result.report) sessionReportsRef.current = [...sessionReportsRef.current, result.report];
      setMessages(cur => [...cur, {
        role: 'assistant', content: summaryLines,
        reportActions: result.report ? [{ type: 'download_report', reportType: 'file-compare', format: 'pptx', label: 'Download Comparison (PPTX)', _blobId: result.report.id, _filename: result.report.filename }] : [],
      }]);
      scheduleAutosave();
    } catch (err) {
      setTyping(false);
      const serverMsg = err?.response?.data?.error || err?.response?.data?.detail || err?.response?.data?.message || '';
      const status = err?.response?.status ? ` (HTTP ${err.response.status})` : '';
      const displayMsg = serverMsg || err.message || 'Unknown error';
      console.error('[Quick Comparison] Failed:', displayMsg, status, err);
      setMessages(cur => [...cur, { role: 'assistant', content: `Comparison failed: ${displayMsg}${status}. Please try again.` }]);
    }
    setNovaContext(prev => ({ ...prev, awaitingReply: null }));
  }, [msalInstance, scheduleAutosave, novaContext.awaitingReply]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    const nextHistory = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextHistory);
    setInput('');
    setTyping(true);
    try {
      // File-compare two-step flow (file supplier name, then target supplier)
      if (novaContext.awaitingReply === 'file-compare-file-supplier' || novaContext.awaitingReply === 'file-compare-supplier') {
        setTyping(false);
        handleFileCompareSupplier(trimmed);
        return;
      }
      // (0) Comparison flow: user naming the FIRST entity (bare intent path)
      if (novaContext.awaitingReply === 'compare-first') {
        const { parts } = extractCompareNames(trimmed);
        const names = parts.length > 0 ? parts : [trimmed.trim()];
        const ck = novaContext.compareKind || 'supplier';
        const kindLabel = { supplier: 'supplier', molecule: 'molecule', area: 'therapeutic area' }[ck] || ck;
        if (names.length >= 2) {
          // User gave multiple names at once → go straight to collector
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `Comparing ${names.join(' vs ')} — add another or generate?`,
            compareCollector: { entityKind: ck, entities: names },
          }]);
          setNovaContext(prev => ({ ...prev, compareSuppliers: names, awaitingReply: null }));
        } else {
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `Got it — "${names[0]}". Which ${kindLabel} would you like to compare them with?`,
          }]);
          setNovaContext(prev => ({ ...prev, compareSuppliers: [names[0]], awaitingReply: 'compare-add' }));
        }
        return;
      }

      // (0a) Comparison flow: user is adding an entity to an ongoing comparison
      if (novaContext.awaitingReply === 'compare-add' && novaContext.compareSuppliers) {
        const { parts } = extractCompareNames(trimmed);
        if (parts.length === 0) {
          const ck = novaContext.compareKind || 'supplier';
          const kindLabel = { supplier: 'supplier', molecule: 'molecule', area: 'therapeutic area' }[ck] || ck;
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `I couldn't extract a ${kindLabel} name from that. Please type just the name.`,
          }]);
          return;
        }
        const merged = [...novaContext.compareSuppliers, ...parts];
        // De-duplicate case-insensitively
        const seen = new Set();
        const allEntities = [];
        for (const e of merged) {
          const key = e.toLowerCase();
          if (!seen.has(key)) { seen.add(key); allEntities.push(e); }
        }
        const ck = novaContext.compareKind || 'supplier';
        setMessages(cur => [...cur, {
          role: 'assistant',
          content: `Comparing ${allEntities.join(' vs ')} — add another or generate?`,
          compareCollector: { entityKind: ck, entities: allEntities },
        }]);
        setNovaContext(prev => ({ ...prev, compareSuppliers: allEntities, awaitingReply: null }));
        return;
      }

      // (0b) Free-text comparison detection: "compare X and Y", "X vs Y", "i want to compare molecules"
      if (COMPARE_INTENT_RE.test(trimmed)) {
        const { kind: detectedKind, parts } = extractCompareNames(trimmed);

        if (parts.length >= 2 && detectedKind) {
          // Kind + 2+ names → go straight to collector with correct kind
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `Comparing ${parts.join(' vs ')} — add another or generate?`,
            compareCollector: { entityKind: detectedKind, entities: parts },
          }]);
          setNovaContext(prev => ({ ...prev, compareSuppliers: parts, compareKind: detectedKind, awaitingReply: null }));
          return;
        }
        if (parts.length >= 2 && !detectedKind) {
          // 2+ names but no kind → use context kind or default to supplier; don't ask
          const ck = novaContext.compareKind || 'supplier';
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `Comparing ${parts.join(' vs ')} — add another or generate?`,
            compareCollector: { entityKind: ck, entities: parts },
          }]);
          setNovaContext(prev => ({ ...prev, compareSuppliers: parts, compareKind: ck, awaitingReply: null }));
          return;
        }
        if (parts.length === 1) {
          // One real entity name → use detected kind, context kind, or default supplier
          const ck = detectedKind || novaContext.compareKind || 'supplier';
          const kindLabel = { supplier: 'supplier', molecule: 'molecule', area: 'therapeutic area' }[ck] || ck;
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `Which ${kindLabel} would you like to compare "${parts[0]}" with? Type the name.`,
          }]);
          setNovaContext(prev => ({ ...prev, compareSuppliers: [parts[0]], compareKind: ck, awaitingReply: 'compare-add' }));
          return;
        }
        if (parts.length === 0 && detectedKind) {
          // Kind word only, no names (e.g. "compare molecules") → start kind-specific collection
          const kindLabel = { supplier: 'supplier', molecule: 'molecule', area: 'therapeutic area' }[detectedKind] || detectedKind;
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `Which ${kindLabel}s would you like to compare? Name the first one.`,
          }]);
          setNovaContext(prev => ({ ...prev, compareSuppliers: [], compareKind: detectedKind, awaitingReply: 'compare-first' }));
          return;
        }
        // No names, no kind → bare comparison intent → ask kind
        setMessages(cur => [...cur, {
          role: 'assistant',
          content: 'Sure — what would you like to compare?',
          compareKindChoice: true,
        }]);
        return;
      }

      // (a) EXPLICIT PRODUCT phrasing → straight to rule-based chat, run actions
      if (PRODUCT_KEYWORDS_RE.test(trimmed)) {
        try {
          const res = await callRuleChat(nextHistory);
          executeRuleChatResponse(res);
          return;
        } catch {
          setMessages(cur => [...cur, { role: 'assistant', content: 'Sorry, I could not reach the database right now. Please try again.' }]);
          return;
        }
      }

      // (b) Call chat-report FIRST — handles explicit reports, dosage-form detection, and LLM classification
      try {
        const rpt = await novaChatReport(msalInstance, trimmed);
        if (rpt.isReport) {
          // Write refusal from intent parser
          if (rpt.writeRefusal) {
            setMessages(cur => [...cur, { role: 'assistant', content: rpt.message }]);
            return;
          }

          // Parsed intent from LLM intent parser → route to existing entity flows
          if (rpt.parsedIntent) {
            if (rpt.action === 'compare') {
              if (!rpt.entityKind && (!rpt.compareEntities || rpt.compareEntities.length === 0)) {
                // No kind stated, no entities → ask what kind
                setMessages(cur => [...cur, {
                  role: 'assistant',
                  content: 'Sure — what would you like to compare?',
                  compareKindChoice: true,
                }]);
                return;
              }
              if (rpt.compareEntities && rpt.compareEntities.length >= 2) {
                const eKind = rpt.entityKind || 'supplier';
                setMessages(cur => [...cur, {
                  role: 'assistant',
                  content: `Comparing ${rpt.compareEntities.join(' vs ')} — add another or generate?`,
                  compareCollector: { entityKind: eKind, entities: rpt.compareEntities },
                }]);
                setNovaContext(prev => ({ ...prev, compareSuppliers: rpt.compareEntities, compareKind: eKind, awaitingReply: null }));
                return;
              }
            }
            if (rpt.action === 'report' || rpt.action === 'list') {
              const kind = rpt.entityKind;
              // Guard: strip kind-word echoed as name (LLM may echo "molecule" as the entityName)
              const KIND_WORDS = new Set(['molecule', 'supplier', 'area', 'form']);
              let name = rpt.entityName;
              if (name && KIND_WORDS.has(name.toLowerCase().trim())) name = null;

              if (kind && !name) {
                // Entity kind known but no specific name → ask the user
                const kindLabels = { molecule: 'molecule', supplier: 'supplier', area: 'therapeutic area', form: 'dosage form' };
                setMessages(cur => [...cur, {
                  role: 'assistant',
                  content: `Which ${kindLabels[kind] || kind} would you like to analyze? Please type the name.`,
                }]);
                return;
              }
              if (kind === 'molecule' && name) {
                setMessages(cur => [...cur, {
                  role: 'assistant',
                  content: `Would you like the product list for "${name}", or a data analysis report?`,
                  ambiguousChoice: { term: name, isMolecule: true, isAtc: false, isForm: false, isSupplier: false,
                    navActions: [{ type: 'navigate', url: `/supplier-products?inn=${encodeURIComponent(name)}` }], csvActions: [], originalMessage: '' },
                }]);
                return;
              }
              if (kind === 'supplier' && name) {
                setMessages(cur => [...cur, {
                  role: 'assistant',
                  content: `Would you like the supplier list for "${name}", or a data analysis report?`,
                  ambiguousChoice: { term: name, isSupplier: true, isAtc: false, isMolecule: false,
                    navActions: [{ type: 'navigate', url: `/suppliers-list?search=${encodeURIComponent(name)}` }], csvActions: [], originalMessage: '' },
                }]);
                return;
              }
              if (kind === 'area' && name) {
                setMessages(cur => [...cur, {
                  role: 'assistant',
                  content: `Would you like the product list for "${name}", or a data analysis report?`,
                  ambiguousChoice: { term: name, isAtc: true, isMolecule: false, isForm: false, isSupplier: false,
                    navActions: [], csvActions: [], originalMessage: '' },
                }]);
                return;
              }
              if (kind === 'form' && name) {
                setMessages(cur => [...cur, {
                  role: 'assistant',
                  content: `Would you like the product list for "${name}", or a data analysis report?`,
                  ambiguousChoice: { term: name, isForm: true, isAtc: false, isMolecule: false, isSupplier: false,
                    navActions: [{ type: 'navigate', url: '/supplier-products', filter: { form: name } }], csvActions: [], originalMessage: '' },
                }]);
                return;
              }
            }
            // parsedIntent with no actionable result → fall through
          }

          // Ambiguous dosage-form term → two-button choice
          if (rpt.ambiguous && rpt.entityType === 'form') {
            setMessages(cur => [...cur, {
              role: 'assistant',
              content: rpt.message,
              ambiguousChoice: {
                term: rpt.term,
                isForm: true,
                isAtc: false,
                isMolecule: false,
                navActions: [{ type: 'navigate', url: '/supplier-products', filter: { form: rpt.term } }],
                csvActions: [],
                originalMessage: rpt.message,
              },
            }]);
            return;
          }
          if (rpt.needsClarification) {
            setMessages(cur => [...cur, { role: 'assistant', content: rpt.message }]);
            return;
          }
          if (rpt.comingSoon) {
            setMessages(cur => [...cur, { role: 'assistant', content: rpt.message }]);
            return;
          }
          // Fuzzy "did you mean?" — entity not found, show candidates
          if (rpt.needsDisambiguation && rpt.disambiguation) {
            const term = Object.keys(rpt.disambiguation)[0];
            const candidates = rpt.disambiguation[term] || [];
            setMessages(cur => [...cur, {
              role: 'assistant',
              content: `"${term}" — did you mean one of these?`,
              reportDisambiguation: { term, candidates, entityKind: rpt.entityKind || 'supplier' },
            }]);
            return;
          }
          // Entity not found at all — show message, do not generate
          if (rpt.notFound) {
            setMessages(cur => [...cur, { role: 'assistant', content: rpt.summary || 'Entity not found. Please check the name.' }]);
            return;
          }
          // For molecule/supplier types, route through the entity sub-choice + language step
          // instead of directly generating (avoids blank reports from unresolved entities)
          if (rpt.reportType === 'molecule' && rpt.inn) {
            setMessages(cur => [...cur, {
              role: 'assistant',
              content: `Would you like the product list for "${rpt.inn}", or a data analysis report?`,
              ambiguousChoice: {
                term: rpt.inn,
                isMolecule: true,
                isAtc: false,
                isForm: false,
                isSupplier: false,
                navActions: [{ type: 'navigate', url: `/supplier-products?inn=${encodeURIComponent(rpt.inn)}` }],
                csvActions: [],
                originalMessage: rpt.summary,
              },
            }]);
            return;
          }
          if (rpt.reportType === 'supplier-portfolio' && rpt.supplier) {
            setMessages(cur => [...cur, {
              role: 'assistant',
              content: `Would you like the product list for "${rpt.supplier}", or a data analysis report?`,
              ambiguousChoice: {
                term: rpt.supplier,
                isSupplier: true,
                isAtc: false,
                isMolecule: false,
                navActions: [{ type: 'navigate', url: `/suppliers-list?search=${encodeURIComponent(rpt.supplier)}` }],
                csvActions: [],
                originalMessage: rpt.summary,
              },
            }]);
            return;
          }
          // Therapeutic-area from LLM classifier → route through the full flow
          // (Product List / Data Analysis → language → 4-button format menu)
          if (rpt.reportType === 'therapeutic-area' && rpt.areas && rpt.areas.length > 0) {
            const areaName = rpt.areas[0];
            setMessages(cur => [...cur, {
              role: 'assistant',
              content: `Would you like the product list for "${areaName}", or a data analysis report?`,
              ambiguousChoice: { term: areaName, isAtc: true, isMolecule: false, isForm: false, isSupplier: false,
                navActions: [], csvActions: [], originalMessage: rpt.summary || '' },
            }]);
            return;
          }
          // For form/predictive/nl-gap types, show the language choice then download
          if (rpt.actions && rpt.actions.length > 0) {
            setMessages(cur => [...cur, {
              role: 'assistant',
              content: 'Which language for the report?',
              languageChoice: {
                reportActions: rpt.actions,
                entity: rpt.summary || 'report',
              },
            }]);
            return;
          }
          // Fallback: show summary (no download actions available)
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: rpt.summary || 'Report ready.',
          }]);
          return;
        }
      } catch {
        // chat-report endpoint failed — fall through to rule-based
      }

      // (c) Fall through: call rule-based chat, inspect intent for ambiguous area/molecule
      try {
        const res = await callRuleChat(nextHistory);

        // Supplier redirect → two-button choice: Product List vs Data Analysis
        if (res.intent === 'supplier_redirect' && (res.currentSupplier || res.matchedSupplier)) {
          const supplierName = res.currentSupplier || res.matchedSupplier;
          const navActions = (res.actions || []).filter(a => a.type === 'navigate');
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `Would you like the product list for "${supplierName}", or a data analysis report?`,
            ambiguousChoice: {
              term: supplierName,
              isSupplier: true,
              isAtc: false,
              isMolecule: false,
              navActions: navActions.length > 0 ? navActions : [{ type: 'navigate', url: `/suppliers-list?search=${encodeURIComponent(supplierName)}` }],
              csvActions: [],
              originalMessage: res.message,
            },
          }]);
          setNovaContext(prev => ({
            currentSupplier: supplierName,
            currentMolecule: prev.currentMolecule,
            lastIntent: 'supplier_redirect',
            awaitingReply: null,
            availableSuppliers: prev.availableSuppliers,
          }));
          return;
        }

        // Disambiguate with an INN match → show Product List vs Data Analysis first
        if (res.intent === 'disambiguate' && res.disambiguateOptions && res.disambiguateOptions.some(o => o.field === 'inn')) {
          const term = res.searchTerm || trimmed;
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: `Would you like the product list for "${term}", or a data analysis report?`,
            ambiguousChoice: {
              term,
              isMolecule: true,
              isAtc: false,
              isForm: false,
              isSupplier: false,
              navActions: (res.actions || []).filter(a => a.type === 'navigate'),
              csvActions: [],
              originalMessage: res.message,
              // Stash the disambiguation data for the Product List branch
              disambiguateOptions: res.disambiguateOptions,
              disambiguateSearchTerm: term,
            },
          }]);
          setNovaContext(prev => ({
            currentSupplier: prev.currentSupplier,
            currentMolecule: term,
            lastIntent: 'molecule',
            awaitingReply: null,
            availableSuppliers: prev.availableSuppliers,
          }));
          return;
        }

        // Fuzzy suggestions — "did you mean?" with clickable buttons
        if (res.intent === 'fuzzy_suggestions' && res.fuzzySuggestions && res.fuzzySuggestions.length > 0) {
          const term = res.searchTerm || trimmed;
          const candidates = res.fuzzySuggestions.map(h => h.name);
          const topKind = res.fuzzySuggestions[0].kind || 'supplier';
          setMessages(cur => [...cur, {
            role: 'assistant',
            content: res.message || `I couldn't find "${term}" — did you mean one of these?`,
            reportDisambiguation: { term, candidates, entityKind: topKind },
          }]);
          return;
        }

        if (AMBIGUOUS_INTENTS.has(res.intent)) {
          const term = res.searchTerm || res.moleculeKeyword || trimmed;
          const isAtc = res.intent === 'atc_search';
          const choiceContent = `Would you like the product list for "${term}", or a data analysis report?`;
          const navActions = (res.actions || []).filter(a => a.type === 'navigate');
          const csvActions = (res.actions || []).filter(a => a.type === 'download_csv');

          setMessages(cur => [...cur, {
            role: 'assistant',
            content: choiceContent,
            ambiguousChoice: {
              term,
              isAtc,
              isMolecule: res.intent === 'molecule',
              navActions,
              csvActions,
              originalMessage: res.message,
            },
          }]);

          setNovaContext(prev => ({
            currentSupplier: res.currentSupplier ?? prev.currentSupplier,
            currentMolecule: res.currentMolecule ?? prev.currentMolecule,
            lastIntent: res.intent || prev.lastIntent,
            awaitingReply: null,
            availableSuppliers: res.uniqueSuppliers && res.uniqueSuppliers.length > 0
              ? res.uniqueSuppliers : prev.availableSuppliers,
          }));
          return;
        }

        // Non-ambiguous intent: render and run actions as normal
        executeRuleChatResponse(res);
      } catch {
        setMessages(cur => [...cur, {
          role: 'assistant',
          content: 'Sorry, I could not reach the database right now. Please try again.',
        }]);
      }
    } finally {
      setTyping(false);
    }
  }, [messages, executeRuleChatResponse, callRuleChat, msalInstance, handleFileCompareSupplier]);

  const handleProductListChoice = useCallback((choice) => {
    // Molecule with disambiguation (INN/ATC field choice) → show disambiguate buttons
    if (choice.disambiguateOptions && choice.disambiguateOptions.length > 0) {
      const term = choice.disambiguateSearchTerm || choice.term;
      const lines = choice.disambiguateOptions.map(o => `\u2022 ${o.label}: ${o.count} products`).join('\n');
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: `"${term}" matches multiple fields:\n${lines}\nWhich field would you like to filter by?`,
        disambiguateOptions: choice.disambiguateOptions,
        searchTerm: term,
      }]);
      return;
    }
    // Run the stashed navigate actions from the rule-based response
    for (const act of choice.navActions || []) {
      const actionNote = runAction(act);
      if (actionNote) {
        setMessages(cur => [...cur, { role: 'assistant', content: actionNote }]);
      }
    }
  }, [runAction]);

  const handleDataAnalysisChoice = useCallback(async (choice, msgIdx) => {
    // Molecule → go straight to language step for the comprehensive molecule report
    if (choice.isMolecule) {
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: 'Which language for the report?',
        languageChoice: {
          reportActions: [
            { type: 'download_report', reportType: 'molecule', format: 'pptx', style: 'focusone', inn: choice.term, label: DYNAMIC_STYLE_ENABLED ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
            ...(DYNAMIC_STYLE_ENABLED ? [{ type: 'download_report', reportType: 'molecule', format: 'pptx', style: 'dynamic', inn: choice.term, label: 'PowerPoint (Dynamic)' }] : []),
            { type: 'download_report', reportType: 'molecule', format: 'xlsx', inn: choice.term, label: 'Download Excel' },
            { type: 'download_report', reportType: 'molecule', format: 'docx', inn: choice.term, label: 'Download Word' },
          ],
          entity: choice.term,
        },
      }]);
      return;
    }

    // Supplier → go straight to language step for the comprehensive supplier report
    if (choice.isSupplier) {
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: 'Which language for the report?',
        languageChoice: {
          reportActions: [
            { type: 'download_report', reportType: 'supplier-portfolio', format: 'pptx', style: 'focusone', supplier: choice.term, label: DYNAMIC_STYLE_ENABLED ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
            ...(DYNAMIC_STYLE_ENABLED ? [{ type: 'download_report', reportType: 'supplier-portfolio', format: 'pptx', style: 'dynamic', supplier: choice.term, label: 'PowerPoint (Dynamic)' }] : []),
            { type: 'download_report', reportType: 'supplier-portfolio', format: 'xlsx', supplier: choice.term, label: 'Download Excel' },
            { type: 'download_report', reportType: 'supplier-portfolio', format: 'docx', supplier: choice.term, label: 'Download Word' },
          ],
          entity: choice.term,
        },
      }]);
      return;
    }

    // Form → ask language before generating
    if (choice.isForm) {
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: 'Which language for the report?',
        languageChoice: {
          reportActions: [
            { type: 'download_report', reportType: 'pharmaceutical-form', format: 'pptx', style: 'focusone', forms: [choice.term], label: DYNAMIC_STYLE_ENABLED ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
            ...(DYNAMIC_STYLE_ENABLED ? [{ type: 'download_report', reportType: 'pharmaceutical-form', format: 'pptx', style: 'dynamic', forms: [choice.term], label: 'PowerPoint (Dynamic)' }] : []),
            { type: 'download_report', reportType: 'pharmaceutical-form', format: 'xlsx', forms: [choice.term], label: 'Download Excel' },
            { type: 'download_report', reportType: 'pharmaceutical-form', format: 'docx', forms: [choice.term], label: 'Download Word' },
          ],
          entity: choice.term,
        },
      }]);
      return;
    }

    // ATC area → ask language before generating
    setMessages(cur => [...cur, {
      role: 'assistant',
      content: 'Which language for the report?',
      languageChoice: {
        reportActions: [
          { type: 'download_report', reportType: 'therapeutic-area', format: 'pptx', style: 'focusone', areas: [choice.term], label: DYNAMIC_STYLE_ENABLED ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
          ...(DYNAMIC_STYLE_ENABLED ? [{ type: 'download_report', reportType: 'therapeutic-area', format: 'pptx', style: 'dynamic', areas: [choice.term], label: 'PowerPoint (Dynamic)' }] : []),
          { type: 'download_report', reportType: 'therapeutic-area', format: 'xlsx', areas: [choice.term], label: 'Download Excel' },
          { type: 'download_report', reportType: 'therapeutic-area', format: 'docx', areas: [choice.term], label: 'Download Word' },
        ],
        entity: choice.term,
      },
    }]);
  }, [msalInstance]);

  // Market Data Analysis — navigate to MACH + format picker + async job
  const handleMarketDataAnalysis = useCallback(async (moleculeTerm, msgIdx) => {
    // Navigate to MACH page with molecule pre-filtered
    navigate(`/market-intelligence/mach?molecule=${encodeURIComponent(moleculeTerm)}`);
    // Show format picker
    setMessages(cur => [...cur, {
      role: 'assistant',
      content: `Navigated to MACH Data for "${moleculeTerm}". Choose a format for the Market Data Analysis report:`,
      marketReportActions: [
        { type: 'market_report', format: 'xlsx', molecule: moleculeTerm, reportScope: 'market', label: 'Download Excel' },
        { type: 'market_report', format: 'pptx', molecule: moleculeTerm, reportScope: 'market', label: 'Download PowerPoint' },
      ],
    }]);
  }, [navigate]);

  // Full Data Analysis — market + supplier combined
  const handleFullDataAnalysis = useCallback(async (moleculeTerm, msgIdx) => {
    navigate(`/market-intelligence/mach?molecule=${encodeURIComponent(moleculeTerm)}`);
    setMessages(cur => [...cur, {
      role: 'assistant',
      content: `Navigated to MACH Data for "${moleculeTerm}". Choose a format for the Full Data Analysis report (Market + Supplier combined):`,
      marketReportActions: [
        { type: 'market_report', format: 'xlsx', molecule: moleculeTerm, reportScope: 'full', label: 'Download Excel (Full)' },
        { type: 'market_report', format: 'pptx', molecule: moleculeTerm, reportScope: 'full', label: 'Download PowerPoint (Full)' },
      ],
    }]);
  }, [navigate]);

  // Handle market/full report download (async job + poll)
  const handleMarketReportDownload = useCallback(async (action, msgIdx) => {
    const key = `mkt-${msgIdx}-${action.format}-${action.reportScope}`;
    setReportGenerating(prev => ({ ...prev, [key]: true }));
    try {
      const submitRes = await novaStartMarketReport(msalInstance, {
        molecule: action.molecule,
        format: action.format,
        reportScope: action.reportScope,
      });
      if (!submitRes.jobId) throw new Error(submitRes.error || 'Failed to start report');

      // Poll
      const pollStartedAt = Date.now();
      setReportProgress({ key, startedAt: pollStartedAt });
      if (reportProgressTimerRef.current) clearInterval(reportProgressTimerRef.current);
      reportProgressTimerRef.current = setInterval(() => setReportProgress(prev => prev ? { ...prev } : null), 1000);

      let meta = null;
      for (let pollN = 0; pollN < 180; pollN++) { // up to 6 min
        await new Promise(r => setTimeout(r, 2000));
        const st = await novaMarketReportStatus(msalInstance, submitRes.jobId);
        if (st.status === 'ready' && st.id) { meta = st; break; }
        if (st.status === 'error') throw new Error(st.error || 'Report generation failed');
      }

      if (reportProgressTimerRef.current) { clearInterval(reportProgressTimerRef.current); reportProgressTimerRef.current = null; }
      setReportProgress(null);
      if (!meta) throw new Error('Report generation timed out');

      // Download
      const blob = await novaDownloadReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = meta.filename || `report.${action.format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessages(cur => [...cur, {
        role: 'assistant',
        content: `Report ready: **${meta.filename}**`,
        reportActions: [{ type: 'download_report', reportType: action.reportScope === 'full' ? 'full-data' : 'market-data', format: action.format, label: 'Download again', _blobId: meta.id, _filename: meta.filename }],
      }]);
      scheduleAutosave();
    } catch (err) {
      if (reportProgressTimerRef.current) { clearInterval(reportProgressTimerRef.current); reportProgressTimerRef.current = null; }
      setReportProgress(null);
      setMessages(cur => [...cur, { role: 'assistant', content: `Report generation failed: ${err.message}` }]);
    } finally {
      setReportGenerating(prev => ({ ...prev, [key]: false }));
    }
  }, [msalInstance, scheduleAutosave]);

  const handleAnalysisTypeChoice = useCallback((atChoice, type) => {
    let pendingActions;
    if (type === 'nlgap') {
      const filters = {};
      if (atChoice.isSupplier) filters.nlGapSupplier = atChoice.term;
      if (atChoice.isMolecule) filters.nlGapInn = atChoice.term;
      pendingActions = [
        { type: 'download_report', reportType: 'nl-gap', format: 'pptx', style: 'focusone', ...filters, label: DYNAMIC_STYLE_ENABLED ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
        ...(DYNAMIC_STYLE_ENABLED ? [{ type: 'download_report', reportType: 'nl-gap', format: 'pptx', style: 'dynamic', ...filters, label: 'PowerPoint (Dynamic)' }] : []),
        { type: 'download_report', reportType: 'nl-gap', format: 'xlsx', ...filters, label: 'Download Excel' },
        { type: 'download_report', reportType: 'nl-gap', format: 'docx', ...filters, label: 'Download Word' },
      ];
    } else if (atChoice.isSupplier) {
      if (type === 'portfolio') {
        pendingActions = [
          { type: 'download_report', reportType: 'supplier-portfolio', format: 'pptx', style: 'focusone', supplier: atChoice.term, label: DYNAMIC_STYLE_ENABLED ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
          ...(DYNAMIC_STYLE_ENABLED ? [{ type: 'download_report', reportType: 'supplier-portfolio', format: 'pptx', style: 'dynamic', supplier: atChoice.term, label: 'PowerPoint (Dynamic)' }] : []),
          { type: 'download_report', reportType: 'supplier-portfolio', format: 'xlsx', supplier: atChoice.term, label: 'Download Excel' },
          { type: 'download_report', reportType: 'supplier-portfolio', format: 'docx', supplier: atChoice.term, label: 'Download Word' },
        ];
      } else {
        pendingActions = [
          { type: 'download_report', reportType: 'predictive', format: 'pptx', style: 'focusone', predictiveSupplier: atChoice.term, label: DYNAMIC_STYLE_ENABLED ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
          ...(DYNAMIC_STYLE_ENABLED ? [{ type: 'download_report', reportType: 'predictive', format: 'pptx', style: 'dynamic', predictiveSupplier: atChoice.term, label: 'PowerPoint (Dynamic)' }] : []),
          { type: 'download_report', reportType: 'predictive', format: 'xlsx', predictiveSupplier: atChoice.term, label: 'Download Excel' },
          { type: 'download_report', reportType: 'predictive', format: 'docx', predictiveSupplier: atChoice.term, label: 'Download Word' },
        ];
      }
    } else if (atChoice.isMolecule) {
      if (type === 'molecule') {
        pendingActions = [
          { type: 'download_report', reportType: 'molecule', format: 'pptx', style: 'focusone', inn: atChoice.term, label: DYNAMIC_STYLE_ENABLED ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
          ...(DYNAMIC_STYLE_ENABLED ? [{ type: 'download_report', reportType: 'molecule', format: 'pptx', style: 'dynamic', inn: atChoice.term, label: 'PowerPoint (Dynamic)' }] : []),
          { type: 'download_report', reportType: 'molecule', format: 'xlsx', inn: atChoice.term, label: 'Download Excel' },
          { type: 'download_report', reportType: 'molecule', format: 'docx', inn: atChoice.term, label: 'Download Word' },
        ];
      } else {
        pendingActions = [
          { type: 'download_report', reportType: 'predictive', format: 'pptx', style: 'focusone', predictiveInn: atChoice.term, label: DYNAMIC_STYLE_ENABLED ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
          ...(DYNAMIC_STYLE_ENABLED ? [{ type: 'download_report', reportType: 'predictive', format: 'pptx', style: 'dynamic', predictiveInn: atChoice.term, label: 'PowerPoint (Dynamic)' }] : []),
          { type: 'download_report', reportType: 'predictive', format: 'xlsx', predictiveInn: atChoice.term, label: 'Download Excel' },
          { type: 'download_report', reportType: 'predictive', format: 'docx', predictiveInn: atChoice.term, label: 'Download Word' },
        ];
      }
    }
    if (pendingActions) {
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: 'Which language for the report?',
        languageChoice: { reportActions: pendingActions, entity: atChoice.term },
      }]);
    }
  }, []);

  // ── Comparison flow handlers ──
  const handleCompareKindChoice = useCallback((kind, pendingEntities) => {
    const kindLabel = { supplier: 'supplier', molecule: 'molecule', area: 'therapeutic area' }[kind] || kind;
    if (pendingEntities && pendingEntities.length >= 2) {
      // Entities were already extracted before kind was known — proceed to collector
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: `Comparing ${pendingEntities.join(' vs ')} — add another or generate?`,
        compareCollector: { entityKind: kind, entities: pendingEntities },
      }]);
      setNovaContext(prev => ({ ...prev, compareSuppliers: pendingEntities, compareKind: kind, awaitingReply: null }));
    } else if (pendingEntities && pendingEntities.length === 1) {
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: `Which ${kindLabel} would you like to compare "${pendingEntities[0]}" with? Type the name.`,
      }]);
      setNovaContext(prev => ({ ...prev, compareSuppliers: pendingEntities, compareKind: kind, awaitingReply: 'compare-add' }));
    } else {
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: `Which ${kindLabel}s would you like to compare? Name the first one.`,
      }]);
      setNovaContext(prev => ({ ...prev, compareSuppliers: [], compareKind: kind, awaitingReply: 'compare-first' }));
    }
  }, []);

  const handleStartCompare = useCallback((firstSupplier) => {
    setMessages(cur => [...cur, {
      role: 'assistant',
      content: `Which supplier would you like to compare "${firstSupplier}" with? Type the name.`,
    }]);
    setNovaContext(prev => ({
      ...prev,
      compareSuppliers: [firstSupplier],
      compareKind: 'supplier',
      awaitingReply: 'compare-add',
    }));
  }, []);

  const handleCompareAddAnother = useCallback((collector) => {
    const entities = collector.entities || collector.suppliers || [];
    const ck = collector.entityKind || 'supplier';
    const kindLabel = { supplier: 'supplier', molecule: 'molecule', area: 'therapeutic area' }[ck] || ck;
    setMessages(cur => [...cur, {
      role: 'assistant',
      content: `Type another ${kindLabel} name to add to the comparison (currently: ${entities.join(', ')}).`,
    }]);
    setNovaContext(prev => ({
      ...prev,
      compareSuppliers: entities,
      compareKind: ck,
      awaitingReply: 'compare-add',
    }));
  }, []);

  const handleCompareGenerate = useCallback((collector) => {
    const entities = collector.entities || collector.suppliers || [];
    const eKind = collector.entityKind || 'supplier';
    let reportType, filterKey;
    if (eKind === 'molecule') {
      reportType = 'molecule-comparison';
      filterKey = 'compareMolecules';
    } else if (eKind === 'area') {
      reportType = 'area-comparison';
      filterKey = 'compareAreas';
    } else {
      reportType = 'supplier-comparison';
      filterKey = 'compareSuppliers';
    }
    const showDynamic = DYNAMIC_STYLE_ENABLED && reportType !== 'supplier-comparison';
    const pendingActions = [
      { type: 'download_report', reportType, format: 'pptx', style: 'focusone', [filterKey]: entities, label: showDynamic ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
      ...(showDynamic ? [{ type: 'download_report', reportType, format: 'pptx', style: 'dynamic', [filterKey]: entities, label: 'PowerPoint (Dynamic)' }] : []),
      { type: 'download_report', reportType, format: 'xlsx', [filterKey]: entities, label: 'Download Excel' },
      { type: 'download_report', reportType, format: 'docx', [filterKey]: entities, label: 'Download Word' },
    ];
    setMessages(cur => [...cur, {
      role: 'assistant',
      content: 'Which language for the report?',
      languageChoice: { reportActions: pendingActions, entity: entities.join(' vs ') },
    }]);
    setNovaContext(prev => ({ ...prev, compareSuppliers: null, awaitingReply: null }));
  }, []);

  /**
   * User picked a disambiguation candidate from fuzzy "did you mean?" buttons.
   * Substitute the canonical name into the original action's entity list and re-trigger generation.
   */
  const handleCompareDisambiguationPick = useCallback((disambData, term, canonicalName) => {
    const action = disambData.action;
    // Build updated entity list: replace the fuzzy term with the canonical pick
    const filterKey = action.compareSuppliers ? 'compareSuppliers' : action.compareMolecules ? 'compareMolecules' : 'compareAreas';
    const original = action[filterKey] || [];
    const updated = original.map(e => e.toLowerCase() === term.toLowerCase() ? canonicalName : e);
    // Check if any other terms still need disambiguation (remove the one we just resolved)
    const remaining = { ...disambData.disambiguation };
    delete remaining[term];
    if (Object.keys(remaining).length > 0) {
      // Still have unresolved terms — show next disambiguation
      const nextTerm = Object.keys(remaining)[0];
      const nextCandidates = remaining[nextTerm];
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: `Got it — "${canonicalName}". Now, "${nextTerm}" — did you mean: ${nextCandidates.join(', ')}?`,
        compareDisambiguation: { disambiguation: remaining, resolved: [...(disambData.resolved || []), canonicalName], action: { ...action, [filterKey]: updated } },
      }]);
    } else {
      // All resolved — go to language choice and generate
      const finalAction = { ...action, [filterKey]: updated };
      const entities = updated;
      const showDynDisambig = DYNAMIC_STYLE_ENABLED && finalAction.reportType !== 'supplier-comparison';
      const pendingActions = [
        { ...finalAction, format: 'pptx', style: 'focusone', label: showDynDisambig ? 'PowerPoint (FocusOne)' : 'Download PowerPoint' },
        ...(showDynDisambig ? [{ ...finalAction, format: 'pptx', style: 'dynamic', label: 'PowerPoint (Dynamic)' }] : []),
        { ...finalAction, format: 'xlsx', label: 'Download Excel' },
        { ...finalAction, format: 'docx', label: 'Download Word' },
      ];
      setMessages(cur => [...cur, {
        role: 'assistant',
        content: `All names confirmed: ${entities.join(' vs ')}. Which language for the report?`,
        languageChoice: { reportActions: pendingActions, entity: entities.join(' vs ') },
      }]);
    }
  }, []);

  /**
   * User picked a fuzzy "did you mean?" candidate from a report disambiguation.
   * Route to the "Product List or Data Analysis?" choice with the resolved name.
   */
  const handleReportDisambiguationPick = useCallback((disambData, canonicalName) => {
    const isSupplier = disambData.entityKind === 'supplier';
    setMessages(cur => [...cur, {
      role: 'assistant',
      content: `Would you like the product list for "${canonicalName}", or a data analysis report?`,
      ambiguousChoice: {
        term: canonicalName,
        isSupplier,
        isMolecule: !isSupplier,
        isAtc: false,
        isForm: false,
        navActions: isSupplier
          ? [{ type: 'navigate', url: `/suppliers-list?search=${encodeURIComponent(canonicalName)}` }]
          : [{ type: 'navigate', url: `/supplier-products?inn=${encodeURIComponent(canonicalName)}` }],
        csvActions: [],
        originalMessage: '',
      },
    }]);
  }, []);

  const handleLanguageChoice = useCallback((langChoice, chosenLang) => {
    const actions = langChoice.reportActions.map(a => ({ ...a, language: chosenLang }));
    setMessages(cur => [...cur, {
      role: 'assistant',
      content: `Generating ${chosenLang === 'nl' ? 'Dutch' : 'English'} report for "${langChoice.entity}"...`,
      reportActions: actions,
    }]);
  }, []);

  const sendQuickReply = useCallback((label) => {
    // Build context-aware message based on current conversation state
    const { currentSupplier, currentMolecule } = novaContext;
    let contextMessage = label;
    if (label === 'Show contact details' && currentSupplier) {
      contextMessage = `Show contact details for ${currentSupplier}`;
    } else if (label === 'Show all products' && currentSupplier) {
      contextMessage = `Show all products of ${currentSupplier}`;
    } else if (label === 'Export as CSV' && currentMolecule) {
      contextMessage = `Export ${currentMolecule} as CSV`;
    } else if (label === 'Export as CSV' && currentSupplier) {
      contextMessage = `Export products of ${currentSupplier} as CSV`;
    } else if (label === 'All suppliers' && currentMolecule) {
      contextMessage = `Show ${currentMolecule} from all suppliers`;
    } else if (label === 'Specific supplier' && currentMolecule) {
      contextMessage = `I want ${currentMolecule} from a specific supplier, which one?`;
    } else if (label === 'Filter by form' && currentMolecule) {
      contextMessage = `Filter ${currentMolecule} by dosage form`;
    } else if (label === 'Filter by form' && currentSupplier) {
      contextMessage = `Filter ${currentSupplier} products by dosage form`;
    } else if (label === 'Open supplier profile' && currentSupplier) {
      contextMessage = `Open supplier profile for ${currentSupplier}`;
    } else if (label === 'Show from all suppliers' && currentMolecule) {
      contextMessage = `Show ${currentMolecule} from all suppliers`;
    } else if (label === 'Start a new query') {
      // Reset context for a fresh turn
      setNovaContext({ currentSupplier: null, currentMolecule: null, lastIntent: null, awaitingReply: null });
      contextMessage = 'What would you like to search next?';
    }
    sendMessage(contextMessage);
  }, [novaContext, sendMessage]);

  const triggerMoleculeSearch = useCallback(() => {
    if (location.pathname !== '/supplier-products') {
      navigate('/supplier-products');
    }
    setMessages(cur => [
      ...cur,
      {
        role: 'assistant',
        content: 'I want to search for a molecule. Which molecule would you like to find?',
      },
    ]);
    setNovaContext(prev => ({ ...prev, lastIntent: 'molecule', awaitingReply: 'molecule-name' }));
  }, [navigate, location.pathname]);

  const handleSupplierSelect = useCallback((supplier, molecule) => {
    const mol = molecule || novaContext.currentMolecule;
    const filter = supplier === 'all'
      ? { search: mol, timestamp: Date.now() }
      : { search: mol, supplier, timestamp: Date.now() };

    sessionStorage.setItem('novaFilter', JSON.stringify(filter));

    if (location.pathname === '/supplier-products') {
      window.dispatchEvent(new CustomEvent('novaFilterUpdate'));
    } else {
      navigate('/supplier-products');
    }

    // Clear supplier buttons
    setNovaContext(prev => ({ ...prev, availableSuppliers: [] }));

    // Add confirmation message
    const msg = supplier === 'all'
      ? `Showing all ${mol} results`
      : `Filtering ${mol} from ${supplier}`;
    setMessages(prev => [...prev, { role: 'assistant', content: msg }]);
  }, [navigate, location.pathname, novaContext.currentMolecule]);

  const handleDisambiguateSelect = useCallback((option, term) => {
    const url = `/supplier-products?${option.param}=${encodeURIComponent(term)}`;
    navigate(url);
    setMessages(prev => [...prev, { role: 'assistant', content: `Filtering by ${option.label} for "${term}".` }]);
  }, [navigate]);

  const handleCSVDownloadAll = useCallback((molecule) => {
    const mol = molecule || novaContext.currentMolecule;
    sendMessage(`Export all ${mol} products as CSV`);
  }, [novaContext.currentMolecule, sendMessage]);

  // ── File upload in chat ──
  const handleFileAttach = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setMessages(cur => [...cur, { role: 'assistant', content: 'File exceeds 50 MB limit. Please choose a smaller file.' }]);
      return;
    }
    pendingFileRef.current = file;
    setMessages(cur => [
      ...cur,
      { role: 'user', content: `Uploaded: ${file.name}` },
      { role: 'assistant', content: `I received "${file.name}". What would you like to do?`, fileChoices: true },
    ]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleFileChoice = useCallback((choice) => {
    const file = pendingFileRef.current;
    if (!file) return;
    if (choice === 'q2') {
      pendingFileRef.current = null;
      setMessages(cur => [...cur, { role: 'user', content: 'Database Injection' }, { role: 'assistant', content: 'Opening the upload form. Please fill in the required fields and submit.' }]);
      window.__novaPendingUploadFile = file;
      window.dispatchEvent(new CustomEvent('novaOpenUploadModal'));
      return;
    }
    setMessages(cur => [
      ...cur,
      { role: 'user', content: 'Quick Comparison' },
      { role: 'assistant', content: "What's the supplier name for this file? (This is used as a display label only — nothing is saved to the database.)" },
    ]);
    setNovaContext(prev => ({ ...prev, awaitingReply: 'file-compare-file-supplier' }));
  }, []);

  const send = () => sendMessage(input);
  const handleExportCsvChip = useCallback(() => {
    if (!DATA_PAGES.some(p => location.pathname.startsWith(p))) {
      setMessages(prev => [...prev,
        { role: 'user', content: 'Export CSV' },
        { role: 'assistant', content: 'Open a data page (Supplier Products, Suppliers, EMA, Dutch, or ANSM) and apply your filters first, then click Export CSV from here or use the page\'s download button.' },
      ]);
      return;
    }
    // Trigger the page's own CSV export via a custom event
    setMessages(prev => [...prev, { role: 'user', content: 'Export CSV' }]);
    const exported = window.dispatchEvent(new CustomEvent('novaTriggerCsvExport'));
    setMessages(prev => [...prev, { role: 'assistant', content: 'Exporting the current filtered view to CSV...' }]);
  }, [location.pathname]);

  const askChip = (chip) => {
    if (chip.action === 'molecule-search') {
      triggerMoleculeSearch();
    } else if (chip.action === 'export-csv') {
      handleExportCsvChip();
    } else {
      sendMessage(chip.q);
    }
  };

  const handleQuickReply = (text) => sendQuickReply(text);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  // Focus trap: move focus into panel on open, return on close
  const panelRef = useRef(null);
  const fabRef = useRef(null);
  useEffect(() => {
    if (open && panelRef.current) {
      const first = panelRef.current.querySelector('button, input, [tabindex]');
      if (first) first.focus();
    }
  }, [open]);

  if (!open && !expanded) return (
    <button ref={fabRef} className="nova-fab" onClick={onClose} aria-label="Open Nova">
      <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    </button>
  );

  let lastAssistantIdx = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === 'assistant') { lastAssistantIdx = i; break; }
  }

  const panelContent = (
    <>
      <div ref={panelRef} className={expanded ? undefined : 'nova-panel open'} role="dialog" aria-label="Nova AI Assistant" style={expanded ? { display: 'flex', flexDirection: 'column', width: '100%', height: '100%', borderRadius: 12, overflow: 'hidden' } : undefined}>
        <div className="nova-hd">
          <div className="nova-av">✦</div>
          <div>
            <div className="nova-n">Nova</div>
            <div className="nova-s">FocusOne AI · Active team member</div>
          </div>
          <div className="nova-online">
            <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor"><circle cx="3" cy="3" r="3" /></svg>
            Online
          </div>
          <button
            className="ms-2 bg-transparent border-0 p-1"
            style={{ color: 'rgba(255,255,255,.6)', fontSize: 14, cursor: 'pointer', lineHeight: 1 }}
            title={sideOpen ? 'Hide sidebar' : 'Show sidebar'}
            onClick={() => { setSideOpen(p => !p); if (!sideOpen) refreshRecentSessions(); }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <button
            className="nova-close-x"
            onClick={onClose}
            aria-label="Close Nova"
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
          {/* ── Left band (collapsible) ── */}
          {sideOpen && (
            <div style={{ width: 180, borderRight: '1px solid var(--border, #eee)', background: 'var(--card, #fff)', display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
              <button
                onClick={() => {
                  scheduleAutosave();
                  const newId = crypto.randomUUID();
                  const newTs = new Date().toISOString();
                  setSessionId(newId);
                  setSessionCreatedAt(newTs);
                  sessionReportsRef.current = [];
                  titleCustomRef.current = false;
                  setMessages(buildInitialMessages(firstName));
                  setNovaContext({ currentSupplier: null, currentMolecule: null, lastIntent: null, awaitingReply: null, availableSuppliers: [] });
                  refreshRecentSessions();
                }}
                style={{ margin: '8px 8px 4px', padding: '6px 0', fontSize: 12, fontWeight: 600, color: 'var(--teal)', background: 'none', border: '1px solid var(--teal)', borderRadius: 6, cursor: 'pointer' }}
              >+ New chat</button>

              <div style={{ fontSize: 10, fontWeight: 600, color: '#999', padding: '8px 10px 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recent</div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, flex: 1 }}>
                {recentSessions.map(s => (
                  <li
                    key={s.sessionId}
                    style={{
                      padding: '6px 10px', cursor: 'pointer', fontSize: 11, position: 'relative',
                      background: s.sessionId === sessionId ? 'var(--teal-bg, #e8f7fa)' : 'transparent',
                      borderLeft: s.sessionId === sessionId ? '3px solid var(--teal)' : '3px solid transparent',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                    onClick={async () => {
                      if (bandRenaming === s.sessionId) return;
                      if (s.sessionId === sessionId) return;
                      scheduleAutosave();
                      await loadSession(s.sessionId);
                      refreshRecentSessions();
                    }}
                    onMouseEnter={e => { const dot = e.currentTarget.querySelector('.nova-kebab'); if (dot) dot.style.opacity = '1'; }}
                    onMouseLeave={e => { const dot = e.currentTarget.querySelector('.nova-kebab'); if (dot && kebabOpen !== s.sessionId) dot.style.opacity = '0'; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {bandRenaming === s.sessionId ? (
                        <input
                          value={bandRenameVal}
                          onChange={e => setBandRenameVal(e.target.value)}
                          onKeyDown={async e => {
                            if (e.key === 'Enter' && bandRenameVal.trim()) {
                              try { await novaRenameSession(msalInstance, s.sessionId, bandRenameVal.trim()); } catch {}
                              setBandRenaming(null);
                              refreshRecentSessions();
                            }
                            if (e.key === 'Escape') setBandRenaming(null);
                          }}
                          onBlur={() => setBandRenaming(null)}
                          onClick={e => e.stopPropagation()}
                          autoFocus
                          style={{ fontSize: 11, width: '100%', border: '1px solid var(--teal)', borderRadius: 3, padding: '1px 4px' }}
                        />
                      ) : (
                        <>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500, color: 'var(--text)' }}>{s.title || 'New chat'}</div>
                          <div style={{ fontSize: 9, color: '#aaa' }}>{s.updatedAt ? new Date(s.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}</div>
                        </>
                      )}
                    </div>
                    {bandRenaming !== s.sessionId && (
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                          className="nova-kebab"
                          onClick={e => { e.stopPropagation(); setKebabOpen(kebabOpen === s.sessionId ? null : s.sessionId); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: 14, lineHeight: 1, color: '#999', opacity: kebabOpen === s.sessionId ? 1 : 0, transition: 'opacity 0.15s' }}
                          aria-label="Session options"
                          aria-haspopup="true"
                        >⋮</button>
                        {kebabOpen === s.sessionId && (
                          <div
                            style={{ position: 'absolute', right: 0, top: 18, background: 'var(--card, #fff)', border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,.12)', zIndex: 20, minWidth: 100 }}
                            onClick={e => e.stopPropagation()}
                          >
                            <button
                              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '6px 12px', fontSize: 11, cursor: 'pointer', color: 'var(--text)' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                              onClick={() => { setBandRenaming(s.sessionId); setBandRenameVal(s.title || ''); setKebabOpen(null); }}
                            >Rename</button>
                            <button
                              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '6px 12px', fontSize: 11, cursor: 'pointer', color: '#E53935' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                              onClick={async () => {
                                setKebabOpen(null);
                                if (!window.confirm('Delete this conversation?')) return;
                                try { await novaDeleteSession(msalInstance, s.sessionId); } catch {}
                                if (s.sessionId === sessionId) {
                                  const newId = crypto.randomUUID();
                                  setSessionId(newId);
                                  setSessionCreatedAt(new Date().toISOString());
                                  sessionReportsRef.current = [];
                                  titleCustomRef.current = false;
                                  setMessages(buildInitialMessages(firstName));
                                  setNovaContext({ currentSupplier: null, currentMolecule: null, lastIntent: null, awaitingReply: null, availableSuppliers: [] });
                                }
                                refreshRecentSessions();
                              }}
                            >Delete</button>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                ))}
                {recentSessions.length === 0 && <li style={{ padding: '6px 10px', fontSize: 11, color: '#bbb' }}>No conversations</li>}
              </ul>

              <button
                onClick={() => { navigate('/chat-history'); onClose(); }}
                style={{ margin: '4px 8px 8px', padding: '6px 0', fontSize: 11, fontWeight: 500, color: '#666', background: 'none', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}
              >All history</button>
            </div>
          )}

          {/* ── Main chat area ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        <div className="nova-msgs" ref={msgsRef}>
          {messages.map((m, i) => {
            const isUser = m.role === 'user';
            const quickReplies = [];
            const msgText = String(m.content || '').toLowerCase();
            const hasMoleculePrompt = msgText.includes('specific one') || msgText.includes('all supplier');
            const dropdownSuppliers = (m.uniqueSuppliers && m.uniqueSuppliers.length > 0)
              ? m.uniqueSuppliers
              : (novaContext.availableSuppliers || []);
            const showDropdown = !isUser
              && !typing
              && dropdownSuppliers.length > 0
              && (m.intent === 'molecule' || (hasMoleculePrompt && novaContext.lastIntent === 'molecule'))
              && !messages.slice(i + 1).some(mm => mm.role === 'user');
            return (
              <div key={i} className={`nm ${isUser ? 'u' : 'b'}`}>
                {!isUser && <div className="nm-av">✦</div>}
                <div className="nm-bub">
                  {isUser
                    ? <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
                    : <div className="nova-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(m.content || ''), { ADD_ATTR: ['target', 'style', 'viewBox', 'points', 'd', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'width', 'height', 'fill', 'stroke', 'stroke-width', 'opacity', 'transform', 'font-size', 'text-anchor', 'font-family', 'preserveAspectRatio'], ADD_TAGS: ['table', 'thead', 'tbody', 'tr', 'th', 'td', 'svg', 'g', 'rect', 'path', 'text', 'polyline', 'polygon', 'circle', 'line', 'defs', 'lineargradient', 'stop', 'tspan'] }) }} />
                  }
                  {m.csvData && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success mt-2 nova-csv-btn"
                      onClick={() => triggerCsvDownload(m.csvData)}
                    >
                      <i className="bi bi-file-earmark-csv me-1"></i>
                      Download {m.csvData.filename || 'export.csv'}
                    </button>
                  )}
                  {(m.actions || []).filter(a => a.type === 'download_csv').map((dlAction, di) => (
                    <button
                      key={di}
                      type="button"
                      className="btn btn-sm btn-outline-success mt-2 nova-csv-btn"
                      onClick={() => {
                        const csvUrl = dlAction.url
                          ? `${import.meta.env.VITE_API_URL}${dlAction.url}`
                          : (dlAction.term ? `${import.meta.env.VITE_API_URL}/api/atc-supplier-search/csv?term=${encodeURIComponent(dlAction.term)}` : null);
                        if (!csvUrl) return;
                        fetch(csvUrl)
                          .then(r => r.blob())
                          .then(blob => {
                            const a = document.createElement('a');
                            a.href = URL.createObjectURL(blob);
                            a.download = dlAction.filename || 'export.csv';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(a.href);
                          })
                          .catch(() => {});
                      }}
                    >
                      <i className="bi bi-download me-1"></i>
                      Download CSV
                    </button>
                  ))}
                  {(m.reportActions || []).map((ra, ri) => {
                    const genKey = `${i}-${ra.format}${ra.style ? '-' + ra.style : ''}`;
                    const isGen = reportGenerating[genKey];
                    return (
                      <button
                        key={ri}
                        type="button"
                        className={`btn btn-sm mt-2 me-2 ${ra.format === 'pptx' && ra.style === 'dynamic' ? 'btn-outline-warning' : ra.format === 'pptx' ? 'btn-outline-primary' : ra.format === 'docx' ? 'btn-outline-info' : 'btn-outline-success'}`}
                        disabled={isGen}
                        onClick={() => handleReportDownload(ra, i)}
                      >
                        <i className={`bi ${ra.format === 'pptx' ? 'bi-file-earmark-slides' : ra.format === 'docx' ? 'bi-file-earmark-word' : 'bi-file-earmark-spreadsheet'} me-1`}></i>
                        {isGen ? 'Generating…' : ra.label}
                      </button>
                    );
                  })}
                  {(m.marketReportActions || []).map((ra, ri) => {
                    const genKey = `mkt-${i}-${ra.format}-${ra.reportScope}`;
                    const isGen = reportGenerating[genKey];
                    return (
                      <button
                        key={`mkt-${ri}`}
                        type="button"
                        className={`btn btn-sm mt-2 me-2 ${ra.format === 'pptx' ? 'btn-outline-primary' : 'btn-outline-success'}`}
                        disabled={isGen}
                        onClick={() => handleMarketReportDownload(ra, i)}
                      >
                        <i className={`bi ${ra.format === 'pptx' ? 'bi-file-earmark-slides' : 'bi-file-earmark-spreadsheet'} me-1`}></i>
                        {isGen ? 'Generating…' : ra.label}
                      </button>
                    );
                  })}
                  {m.fileChoices && !messages.slice(i + 1).some(mm => mm.role === 'user') && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button type="button" onClick={() => handleFileChoice('q1')} style={{ background: 'var(--teal)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>Quick Comparison</button>
                      <button type="button" onClick={() => handleFileChoice('q2')} style={{ background: 'var(--card)', color: 'var(--teal)', border: '1px solid var(--teal)', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>Database Injection</button>
                    </div>
                  )}
                  {m.ambiguousChoice && !messages.slice(i + 1).some(mm => mm.role === 'user') && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {m.ambiguousChoice.isMolecule && (
                        <button
                          type="button"
                          onClick={() => {
                            const mol = m.ambiguousChoice.term;
                            const nextMsgs = [...messages, { role: 'assistant', content: `Opening the Molecule Cockpit for “${mol}” — see the page on the left.` }];
                            const nextCtx = { ...novaContext, currentMolecule: mol, currentSupplier: null, lastIntent: 'molecule', awaitingReply: null };
                            setMessages(nextMsgs); setNovaContext(nextCtx); saveSessionNow(nextMsgs, nextCtx);
                            navigate(`/cockpit/molecule/${encodeURIComponent(mol)}`);
                          }}
                          style={{ background: 'linear-gradient(135deg, var(--teal), var(--navy))', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                        >
                          🧬 Open Molecule Cockpit
                        </button>
                      )}
                      {m.ambiguousChoice.isSupplier && (
                        <button
                          type="button"
                          onClick={() => {
                            const sup = m.ambiguousChoice.term;
                            const nextMsgs = [...messages, { role: 'assistant', content: `Opening the Supplier Cockpit for “${sup}” — see the page on the left.` }];
                            const nextCtx = { ...novaContext, currentSupplier: sup, currentMolecule: null, lastIntent: 'supplier', awaitingReply: null };
                            setMessages(nextMsgs); setNovaContext(nextCtx); saveSessionNow(nextMsgs, nextCtx);
                            // warm the slow news job so it has a head start while the intro plays
                            try { fetch(`${import.meta.env.VITE_API_URL}/api/nova/supplier-news`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ supplier: sup }) }); } catch { /* ignore */ }
                            navigate(`/cockpit/supplier/${encodeURIComponent(sup)}`);
                            // fetch a condensed summary card into the chat and persist immediately
                            (async () => {
                              try {
                                const r = await fetch(`${import.meta.env.VITE_API_URL}/api/nova/cockpit-summary?supplier=${encodeURIComponent(sup)}`);
                                if (r.ok) { const d = await r.json(); setMessages(cur => { const m2 = [...cur, { role: 'assistant', content: buildNovaSummaryHtml(d) }]; saveSessionNow(m2, nextCtx); return m2; }); }
                              } catch { /* ignore */ }
                            })();
                          }}
                          style={{ background: 'linear-gradient(135deg, var(--teal), var(--navy))', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                        >
                          🚀 Open Supplier Cockpit
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleProductListChoice(m.ambiguousChoice)}
                        style={{ background: 'var(--teal)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        {m.ambiguousChoice.isSupplier ? 'Supplier List' : 'Product List'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDataAnalysisChoice(m.ambiguousChoice, i)}
                        style={{ background: 'var(--navy)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        {m.ambiguousChoice.isSupplier ? 'Data Analysis' : 'Supplier Data Analysis'}
                      </button>
                      {m.ambiguousChoice.isMolecule && (
                        <button
                          type="button"
                          onClick={() => handleMarketDataAnalysis(m.ambiguousChoice.term, i)}
                          style={{ background: '#156082', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                        >
                          Market Data Analysis
                        </button>
                      )}
                      {m.ambiguousChoice.isMolecule && (
                        <button
                          type="button"
                          onClick={() => handleFullDataAnalysis(m.ambiguousChoice.term, i)}
                          style={{ background: '#0B3D6B', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                        >
                          Full Data Analysis
                        </button>
                      )}
                      {m.ambiguousChoice.isSupplier && (
                        <button
                          type="button"
                          onClick={() => handleStartCompare(m.ambiguousChoice.term)}
                          style={{ background: '#0B3D6B', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                        >
                          Compare with another
                        </button>
                      )}
                    </div>
                  )}
                  {m.analysisTypeChoice && !messages.slice(i + 1).some(mm => mm.role === 'user') && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => handleAnalysisTypeChoice(m.analysisTypeChoice, m.analysisTypeChoice.isSupplier ? 'portfolio' : 'molecule')}
                        style={{ background: 'var(--teal)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        {m.analysisTypeChoice.isSupplier ? 'Portfolio Evolution' : 'Molecule Analysis'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAnalysisTypeChoice(m.analysisTypeChoice, 'predictive')}
                        style={{ background: 'var(--navy)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        Predictive Analysis
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAnalysisTypeChoice(m.analysisTypeChoice, 'nlgap')}
                        style={{ background: '#0B3D6B', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        NL Market Gap
                      </button>
                    </div>
                  )}
                  {m.languageChoice && !messages.slice(i + 1).some(mm => mm.role === 'user') && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => handleLanguageChoice(m.languageChoice, 'en')}
                        style={{ background: 'var(--teal)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        English
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLanguageChoice(m.languageChoice, 'nl')}
                        style={{ background: 'var(--navy)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        Dutch
                      </button>
                    </div>
                  )}
                  {m.compareKindChoice && !messages.slice(i + 1).some(mm => mm.role === 'user') && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button type="button" onClick={() => handleCompareKindChoice('supplier', m.pendingCompareEntities)}
                        style={{ background: 'var(--teal)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                        Suppliers
                      </button>
                      <button type="button" onClick={() => handleCompareKindChoice('molecule', m.pendingCompareEntities)}
                        style={{ background: 'var(--navy)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                        Molecules
                      </button>
                      <button type="button" onClick={() => handleCompareKindChoice('area', m.pendingCompareEntities)}
                        style={{ background: '#0B3D6B', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                        Therapeutic Areas
                      </button>
                    </div>
                  )}
                  {m.compareCollector && !messages.slice(i + 1).some(mm => mm.role === 'user') && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => handleCompareAddAnother(m.compareCollector)}
                        style={{ background: 'var(--teal)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        Add another
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCompareGenerate(m.compareCollector)}
                        style={{ background: 'var(--navy)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        Generate
                      </button>
                    </div>
                  )}
                  {m.compareDisambiguation && !messages.slice(i + 1).some(mm => mm.role === 'user') && (
                    <div style={{ marginTop: 10 }}>
                      {Object.entries(m.compareDisambiguation.disambiguation).map(([term, candidates]) => (
                        <div key={term} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>&quot;{term}&quot; — did you mean:</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {candidates.map(c => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => handleCompareDisambiguationPick(m.compareDisambiguation, term, c)}
                                style={{ background: 'var(--teal)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                              >
                                {c}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {m.reportDisambiguation && !messages.slice(i + 1).some(mm => mm.role === 'user') && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>&quot;{m.reportDisambiguation.term}&quot; — did you mean:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {m.reportDisambiguation.candidates.map(c => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => handleReportDisambiguationPick(m.reportDisambiguation, c)}
                            style={{ background: 'var(--teal)', color: 'var(--card)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {showDropdown && (
                    <SupplierButtons
                      suppliers={dropdownSuppliers}
                      molecule={m.moleculeKeyword || novaContext.currentMolecule}
                      onSelect={handleSupplierSelect}
                      onDownloadAll={handleCSVDownloadAll}
                    />
                  )}
                  {!isUser && m.disambiguateOptions && m.disambiguateOptions.length > 0 && !messages.slice(i + 1).some(mm => mm.role === 'user') && (
                    <DisambiguateButtons
                      options={m.disambiguateOptions}
                      searchTerm={m.searchTerm}
                      onSelect={handleDisambiguateSelect}
                    />
                  )}
                  {quickReplies.length > 0 && (
                    <div className="nova-quick-replies">
                      {quickReplies.map(r => (
                        <button
                          key={r}
                          type="button"
                          className="nova-qr-btn"
                          onClick={() => handleQuickReply(r)}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {typing && (
            <div className="nm b">
              <div className="nm-av">✦</div>
              <div className="nm-bub">
                <div className="nova-thinking-label">Nova is thinking…</div>
                <span className="typing"><span /><span /><span /></span>
              </div>
            </div>
          )}
          {reportProgress && (() => {
            const elapsed = Math.floor((Date.now() - reportProgress.startedAt) / 1000);
            const mins = Math.floor(elapsed / 60);
            const secs = elapsed % 60;
            const stage = [...REPORT_PROGRESS_STAGES].reverse().find(s => elapsed >= s.after) || REPORT_PROGRESS_STAGES[0];
            return (
              <div className="nm b">
                <div className="nm-av">✦</div>
                <div className="nm-bub" style={{ width: '100%' }}>
                  <div style={{ fontSize: 12, color: 'var(--text)', marginBottom: 6, fontWeight: 500 }}>{stage.text}</div>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--border, #e0e0e0)', overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{ height: '100%', borderRadius: 2, background: 'var(--teal)', animation: 'novaProgressPulse 1.8s ease-in-out infinite', width: '40%' }} />
                  </div>
                  <div style={{ fontSize: 10, color: '#999' }}>{mins > 0 ? `${mins}m ${String(secs).padStart(2, '0')}s` : `${secs}s`} elapsed</div>
                  <div className="nova-skel-line w90" />
                  <div className="nova-skel-line w80" />
                  <div className="nova-skel-line w60" />
                </div>
              </div>
            );
          })()}
        </div>

        <div className="nova-ir">
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.doc,.docx" onChange={handleFileAttach} />
          <button className="nova-send" style={{ marginRight: 4 }} onClick={() => fileInputRef.current?.click()} title="Attach file" aria-label="Attach file">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></svg>
          </button>
          <input
            ref={inputRef}
            className="nova-in"
            placeholder="Ask Nova anything…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
          />
          {speech.supported && (
            <>
              <button
                className="nova-mic-lang"
                onClick={toggleMicLang}
                title={`Speech language: ${micLang === 'en-US' ? 'English' : 'Dutch'} (click to switch)`}
              >
                {micLang === 'en-US' ? 'EN' : 'NL'}
              </button>
              <button
                className={`nova-mic${speech.listening ? ' listening' : ''}`}
                onClick={toggleMic}
                aria-label={speech.listening ? 'Stop listening' : 'Voice input'}
                title={speech.listening ? 'Stop listening' : 'Voice input'}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                  <path d="M19 10v2a7 7 0 01-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>
            </>
          )}
          <button className="nova-send" onClick={send}>
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
        {speech.error && <div className="nova-mic-err">{speech.error}</div>}

          </div>{/* close main chat area wrapper */}
        </div>{/* close flex row */}
      </div>
    </>
  );

  if (expanded) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.5)' }} onClick={onClose}>
        <div style={{ width: '80vw', height: '80vh', maxWidth: 1200, maxHeight: 800, display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
          {panelContent}
        </div>
      </div>
    );
  }

  return panelContent;
}
